import { App } from "vue"
import { createI18n } from "vue-i18n"
import type { Lang, Locale } from "@/service/translate/flights/flightTranslations.types"
import flightsTranslations from "@/service/translate/flights"
import trainTranslations from "@/service/translate/train"
import summeryTranslations from "@/service/translate/summery"
import paymentTranslations from "@/service/translate/payment"
import transferTranslations from "@/service/translate/transfer"
import optionsTranslations from "@/service/translate/options"
import commonTranslations from "@/service/translate/common"
import balancePaymentTranslations from "@/service/translate/balancePayment"
import type { i18nMessages } from '@/service/translate/translate.types'


const messages: Partial<i18nMessages> = {
    "fr": {
        ...flightsTranslations.fr,
        ...summeryTranslations.fr,
        ...paymentTranslations.fr,
        ...transferTranslations.fr,
        ...optionsTranslations.fr,
        ...commonTranslations.fr,
        ...trainTranslations.fr,
        ...balancePaymentTranslations.fr,
    },
    "en": {
        ...flightsTranslations.uk,
        ...summeryTranslations.uk,
        ...paymentTranslations.uk,
        ...transferTranslations.uk,
        ...optionsTranslations.uk,
        ...commonTranslations.uk,
        ...trainTranslations.uk,
        ...balancePaymentTranslations.uk,
    },
    "nl": {
        ...flightsTranslations.nl,
        ...summeryTranslations.nl,
        ...paymentTranslations.nl,
        ...transferTranslations.nl,
        ...optionsTranslations.nl,
        ...commonTranslations.nl,
        ...trainTranslations.nl,
        ...balancePaymentTranslations.nl,
    },
    "yoonly-fr": {
        ...summeryTranslations['yoonly-fr'],
        ...optionsTranslations['yoonly-fr'],
        ...commonTranslations['yoonly-fr'],
        ...paymentTranslations['yoonly-fr'],
    },
    "yoonly-nl": {
        ...summeryTranslations['yoonly-nl'],
        ...optionsTranslations['yoonly-nl'],
        ...commonTranslations['yoonly-nl'],
        ...paymentTranslations['yoonly-nl'],
    }
};
const datetimeFormats = {
    "fr": {
        short2: {
            weekday: 'short', day: 'numeric', month: 'short'
        },
        short: {
            day: '2-digit', month: '2-digit', year: 'numeric' // DD/MM/YYYY
        },
        long: {
            weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' // e.g., lun. 06 janv. 2025
        },
        long2: {
            weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric'
        },
    },
    "en": {
        short2: {
            weekday: 'short', day: 'numeric', month: 'short'
        },
        short: {
            day: '2-digit', month: '2-digit', year: 'numeric' // DD/MM/YYYY
        },
        long: {
            day: '2-digit', month: 'short', year: 'numeric' // e.g., 06 Jan 2025
        },
        long2: {
            weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric'
        },
    },
    "en-GB": {
        short2: {
            weekday: 'short', day: 'numeric', month: 'short'
        },
        short: {
            day: '2-digit', month: '2-digit', year: 'numeric' // DD/MM/YYYY
        },
        long: {
            day: '2-digit', month: 'short', year: 'numeric' // e.g., 06 Jan 2025
        },
        long2: {
            weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric'
        },
    },
    "nl": {
        short2: {
            weekday: 'short', day: 'numeric', month: 'short'
        },
        short: {
            day: '2-digit', month: '2-digit', year: 'numeric' // Matches fr-FR: DD/MM/YYYY
        },
        long: {
            weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' // Matches fr-FR long format
        },
        long2: {
            weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric'
        },
    },
    "yoonly-fr": {
        short2: {
            weekday: 'short', day: 'numeric', month: 'short'
        },
        short: {
            day: '2-digit', month: '2-digit', year: 'numeric'
        },
        long: {
            weekday: 'short', day: '2-digit', month: 'short', year: 'numeric'
        },
        long2: {
            weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric'
        },
    },
    "yoonly-nl": {
        short2: {
            weekday: 'short', day: 'numeric', month: 'short'
        },
        short: {
            day: '2-digit', month: '2-digit', year: 'numeric'
        },
        long: {
            weekday: 'short', day: '2-digit', month: 'short', year: 'numeric'
        },
        long2: {
            weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric'
        },
    }
};
const numberFormats = {
    'fr': {
        currency: {
            style: 'currency',
            currency: 'EUR',
            currencyDisplay: 'symbol',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }
    },
    'en': {
        currency: {
            style: 'currency',
            currency: 'GBP',
            currencyDisplay: 'symbol',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }
    },
    'nl': {
        currency: {
            style: 'currency',
            currency: 'EUR',
            currencyDisplay: 'symbol',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }
    },
    'yoonly-fr': {
        currency: {
            style: 'currency',
            currency: 'EUR',
            currencyDisplay: 'symbol',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }
    },
    'yoonly-nl': {
        currency: {
            style: 'currency',
            currency: 'EUR',
            currencyDisplay: 'symbol',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }
    },
}
const initI18n = (app: App<Element>, lang: Lang) => {
    const i18n = createI18n({
        legacy: false,
        locale: lang,
        fallbackLocale: {
            'yoonly-fr': ['fr'],
            'yoonly-nl': ['nl', 'fr'],
            'default': ['fr']
        },
        messages: messages as any,
        datetimeFormats: datetimeFormats as any,
        numberFormats: numberFormats as any,
        silentTranslationWarn: true,
        silentFallbackWarn: true
    })

    app.use(i18n)
}


export default initI18n